import {
    Action,
    getModule,
    Module,
    Mutation,
    MutationAction,
    VuexModule,
} from 'vuex-module-decorators';
import {
    AlgoType,
    BehaviorFields,
    Device,
    DeviceData,
    DeviceInformation,
    DevicesState,
    HeatingAlgoFields,
    MigrationData,
    OffsetKey,
    TimelineEvent,
    TransferSourceItem,
    TransformedMigrationData,
} from '~/interfaces/device.interface';
import apiService from '~/services/api.service';
import { DeviceGroupModule } from '~/store/modules/devicegroup';
import store from '~common-store';

// MODEL IMPORTS
import { NADevice } from '~/models/NADevice.model';

// GATEWAYS

// netatmo
import { NAC } from '~/models/gateways/netatmo/NAC.model';
import { NACamera } from '~/models/gateways/netatmo/NACamera.model';
import { NAMain } from '~/models/gateways/netatmo/NAMain.model';
import { NAPlug } from '~/models/gateways/netatmo/NAPlug.model';
import { NAVaillant } from '~/models/gateways/netatmo/NAVaillant.model';
import { NCO } from '~/models/gateways/netatmo/NCO.model';
import { NDB } from '~/models/gateways/netatmo/NDB.model';
import { NDL } from '~/models/gateways/netatmo/NDL.model';
import { NHC } from '~/models/gateways/netatmo/NHC.model';
import { NOC } from '~/models/gateways/netatmo/NOC.model';
import { NOC2 } from '~/models/gateways/netatmo/NOC2.model';
import { NPC } from '~/models/gateways/netatmo/NPC.model';
import { NSD } from '~/models/gateways/netatmo/NSD.model';
import { OTH } from '~/models/gateways/netatmo/OTH.model';

// legrand
import { BAC } from '~/models/gateways/legrand/BAC.model';
import { BNCX } from '~/models/gateways/legrand/BNCX.model';
import { BNHY } from '~/models/gateways/legrand/BNHY.model';
import { BNMH } from '~/models/gateways/legrand/BNMH.model';
import { BNS } from '~/models/gateways/legrand/BNS.model';
import { NBG } from '~/models/gateways/legrand/NBG.model';
import { NLE } from '~/models/gateways/legrand/NLE.model';
import { NLG } from '~/models/gateways/legrand/NLG.model';

// velux
import { NXG } from '~/models/gateways/velux/NXG.model';

// muller
import { NMG } from '~/models/gateways/muller/NMG.model';

// engie
import { ELG } from '~/models/gateways/engie/ELG.model';

// somfy
import { TPSG } from '~/models/gateways/somfy/TPSG.model';

// MODULES
import { NAModule } from '~/models/modules/NAModule.model';

// netatmo
import { NTG } from '~/models/gateways/legrand/NTG.model';
import { EBU } from '~/models/modules/netatmo/EBU.model';
import { NACamDoorTag } from '~/models/modules/netatmo/NACamDoorTag.model';
import { NAModule1 } from '~/models/modules/netatmo/NAModule1.model';
import { NAModule2 } from '~/models/modules/netatmo/NAModule2.model';
import { NAModule3 } from '~/models/modules/netatmo/NAModule3.model';
import { NAModule4 } from '~/models/modules/netatmo/NAModule4.model';
import { NATherm1 } from '~/models/modules/netatmo/NATherm1.model';
import { NAThermVaillant } from '~/models/modules/netatmo/NAThermVaillant.model';
import { NIS } from '~/models/modules/netatmo/NIS.model';
import { NRV } from '~/models/modules/netatmo/NRV.model';
import { OTM } from '~/models/modules/netatmo/OTM.model';

// engie
import { ELK } from '~/models/modules/engie/ELK.model';

// legrand
import { BNDL } from '~/models/modules/legrand/BNDL.model';
import { LegrandModule } from '~/models/modules/legrand/LegrandModule.model';
import { MultipleGangSwitch } from '~/models/modules/legrand/MultipleGangSwitch.model';
import { NLAV } from '~/models/modules/legrand/NLAV.model';
import { NLD } from '~/models/modules/legrand/NLD.model';
import { NLEModule } from '~/models/modules/legrand/NLEModule.model';
import { NLLV } from '~/models/modules/legrand/NLLV.model';
import { NLOC } from '~/models/modules/legrand/NLOC.model';
import { NLPC } from '~/models/modules/legrand/NLPC.model';
import { NLT } from '~/models/modules/legrand/NLT.model';
import { NLTS } from '~/models/modules/legrand/NLTS.model';
import { NLWO } from '~/models/modules/legrand/NLWO.model';
import { WirelessSwitch } from '~/models/modules/legrand/WirelessSwitch.model';
import { WirelessSwitch2Buttons } from '~/models/modules/legrand/WirelessSwitch2Buttons.model';

// velux
import { NXB } from '~/models/modules/velux/NXB.model';
import { NXD } from '~/models/modules/velux/NXD.model';
import { NXO } from '~/models/modules/velux/NXO.model';
import { NXS } from '~/models/modules/velux/NXS.model';

// muller
import { NMH } from '~/models/modules/muller/NMH.model';
import { NMR } from '~/models/modules/muller/NMR.model';

// bubendorff
import { macToBase10 } from '~/helpers';
import { BNC1 } from '~/models/gateways/legrand/BNC1.model';
import { BNC3 } from '~/models/gateways/legrand/BNC3.model';
import { BPAC } from '~/models/gateways/legrand/BPAC.model';
import { BPVC } from '~/models/gateways/legrand/BPVC.model';
import { NLEVSE } from '~/models/gateways/legrand/NLEVSE.model';
import { MFH } from '~/models/gateways/mfh/MFH.model';
import { NIE } from '~/models/gateways/netatmo/NIE.model';
import { NOP } from '~/models/gateways/netatmo/NOP.model';
import { BubendorffModule } from '~/models/modules/bubendorff/BubendorffModule.model';
import { NLC } from '~/models/modules/legrand/NLC.model';
import { NLDC } from '~/models/modules/legrand/NLDC.model';
import { NLFE } from '~/models/modules/legrand/NLFE.model';
import { NLFN } from '~/models/modules/legrand/NLFN.model';
import { NLFO } from '~/models/modules/legrand/NLFO.model';
import { NLH } from '~/models/modules/legrand/NLH.model';
import { NLIB } from '~/models/modules/legrand/NLIB.model';
import { NLL } from '~/models/modules/legrand/NLL.model';
import { NLLF } from '~/models/modules/legrand/NLLF.model';
import { NLM } from '~/models/modules/legrand/NLM.model';
import { NLP } from '~/models/modules/legrand/NLP.model';
import { NLPD } from '~/models/modules/legrand/NLPD.model';
import { NLPO } from '~/models/modules/legrand/NLPO.model';
import { NLUF } from '~/models/modules/legrand/NLUF.model';
import { NLUI } from '~/models/modules/legrand/NLUI.model';
import { NLUO } from '~/models/modules/legrand/NLUO.model';
import { NLUP } from '~/models/modules/legrand/NLUP.model';
import { NLY } from '~/models/modules/legrand/NLY.model';
import { Sensor } from '~/models/modules/legrand/Sensor.model';
import { MullerModule } from '~/models/modules/muller/MullerModule.model';
import { OwnerModule } from '~/store/modules/owners';
import { RecurseObject } from '~common-interfaces/helper.interface';

export function createDevice(device: DeviceData): NADevice {
    switch (device.type) {
        // GATEWAYS

        // netatmo
        case 'NACamera':
            return new NACamera(device);
        case 'NAMain':
            return new NAMain(device);
        case 'NTG':
            return new NTG(device);
        case 'NAPlug':
            return new NAPlug(device);
        case 'NAVaillant':
            return new NAVaillant(device);
        case 'OTH':
            return new OTH(device);
        case 'NOC':
            return new NOC(device);
        case 'NOC2':
            return new NOC2(device);
        case 'NOP':
            return new NOP(device);
        case 'NIE':
            return new NIE(device);
        case 'NDB':
            return new NDB(device);
        case 'NCO':
            return new NCO(device);
        case 'NSD':
            return new NSD(device);
        case 'NAC':
            return new NAC(device);
        case 'NDL':
            return new NDL(device);
        case 'NHC':
            return new NHC(device);
        case 'NPC':
            return new NPC(device);
        // legrand
        case 'NLG':
            return new NLG(device);
        case 'BNCX':
            return new BNCX(device);
        case 'NLE':
            // NLE type can be both a gateway or a module
            return device.is_module ? new NLEModule(device) : new NLE(device);
        case 'NBG':
            return new NBG(device);
        case 'BNS':
            return new BNS(device);
        case 'BAC':
            return new BAC(device);
        case 'BNMH':
            return new BNMH(device);
        case 'BNHY':
            return new BNHY(device);
        // velux
        case 'NXG':
            return new NXG(device);
        // muller
        case 'NMG':
            return new NMG(device);
        // engie
        case 'ELG':
            return new ELG(device);
        // somfy
        case 'TPSG':
            return new TPSG(device);
        // mfh
        case 'MFH':
            return new MFH(device);
        case 'BFII':
        case 'BPVC':
            return new BPVC(device);
        case 'BPAC':
            return new BPAC(device);
        case 'BNC1':
        case 'BDIY':
            return new BNC1(device);
        case 'BNC3':
            return new BNC3(device);
        case 'NLEVSE':
            return new NLEVSE(device);
            // MODULES

        // netatmo
        case 'NAModule1':
            return new NAModule1(device);
        case 'NAModule2':
            return new NAModule2(device);
        case 'NAModule3':
            return new NAModule3(device);
        case 'NAModule4':
            return new NAModule4(device);
        case 'NAThermVaillant':
            return new NAThermVaillant(device);
        case 'NATherm1':
            return new NATherm1(device);
        case 'OTM':
            return new OTM(device);
        case 'NIS':
            return new NIS(device);
        case 'NACamDoorTag':
            return new NACamDoorTag(device);
        case 'NRV':
            return new NRV(device);
        case 'EBU':
            return new EBU(device);
        // engie
        case 'ELK':
            return new ELK(device);
        // legrand
        case 'BNDL':
            return new BNDL(device);
        case 'BNEU':
        case 'BNSL':
        case 'BNIL':
        case 'BNLD':
        case 'BNAB':
        case 'BNAS':
        case 'BNMS':
        case 'BNTH':
        case 'BNTR':
        case 'BNFC':
        case 'BNCS':
        case 'BNLO':
        case 'BNXM':
        case 'BN3L':
            return new NAModule(device);
        case 'NLAD':
        case 'NLAS':
            return new WirelessSwitch(device);
        case 'NLAO':
            return new WirelessSwitch2Buttons(device);
        case 'NLWO':
            return new NLWO(device);
        case 'NLD':
            return new NLD(device);
        case 'NLAV':
            return new NLAV(device);
        case 'NLOC':
            return new NLOC(device);
        case 'NLPC':
            return new NLPC(device);
        case 'NLTS':
            return new NLTS(device);
        case 'NLFE':
            return new NLFE(device);
        case 'NLFN':
            return new NLFN(device);
        case 'NLL':
            return new NLL(device);
        case 'NLLF':
            return new NLLF(device);
        case 'NLM':
            return new NLM(device);
        case 'NLUF':
            return new NLUF(device);
        case 'NLUI':
            return new NLUI(device);
        case 'NLUO':
            return new NLUO(device);
        case 'NLUP':
            return new NLUP(device);
        case 'NLFO':
            return new NLFO(device);
        case 'NLT':
        case 'NLDP':
        case 'NLDD':
            return new NLT(device);
        case 'MGSO':
        case 'MGSI':
        case 'MGSC':
        case 'MGSW':
            return new Sensor(device);
        case 'NLDC':
            return new NLDC(device);
        case 'NLPD':
            return new NLPD(device);
        case 'NLH':
            return new NLH(device);
        case 'NLIB':
            return new NLIB(device);
        case 'NLC':
            return new NLC(device);
        case 'NLP':
            return new NLP(device);
        case 'NLPO':
            return new NLPO(device);
        case 'NLLV':
            return new NLLV(device);
        case 'NLIS':
        case 'NLIV':
            return new MultipleGangSwitch(device);
        case 'NLY':
            return new NLY(device);
        // velux
        case 'NXO':
            return new NXO(device);
        case 'NXS':
            return new NXS(device);
        case 'NXB':
            return new NXB(device);
        case 'NXD':
            return new NXD(device);
        // muller
        case 'NMH':
            return new NMH(device);
        case 'NMR':
            return new NMR(device);
        case 'NMT':
        case 'NMW':
            return new MullerModule(device);
        // bubendorff
        case 'NBO':
        case 'NBR':
        case 'NBS':
            return new BubendorffModule(device);
        case 'NTDC':
            return new LegrandModule(device);

        default:
            if (device.type.startsWith('NL') || device.type.startsWith('Z3')) {
                return new LegrandModule(device);
            }
            return new NADevice(device);
    }
}

@Module({ dynamic: true, store, name: 'devices', namespaced: true })
class Devices extends VuexModule implements DevicesState {

    currentGatewayId = '';
    currentModuleId = '';
    devices: Device [] = [];
    deviceInformationArray: DeviceInformation[] = [];
    dumpValue = '';
    dumpLogsValue = '';
    modelDumpValue = '';
    diagnosisValue = '';
    statsDumpValue: { dump: string, verbose: boolean } = { dump: '', verbose: false };
    localResources: RecurseObject = {};
    timelineEvents: TimelineEvent[] = [];
    logsValue: any[] = [];
    gatewayMigrationData: TransformedMigrationData = { sources: [], selectedSource: null, target: '' };
    modulesMigrationData: TransformedMigrationData[] = [];

    get currentGateway(): Device | undefined {
        return this.getDeviceById(this.currentGatewayId);
    }

    get currentModule(): Device | undefined {
        return this.getDeviceById(this.currentModuleId);
    }

    get gatewaysFiltered(): Device[] {
        return this.devices.filter((device) => !device.is_module);
    }

    get modulesFiltered(): Device[] {
        return this.devices.filter((device) => device.is_module);
    }

    get getDeviceById() {
        return (deviceId: string, gatewayId?: string): Device | undefined => this.devices.find((device) => {
            if (device.id !== deviceId) {
                return false;
            }
            // if device has a bridge, check that it corresponds to the current gateway id
            // as it could be a module that's paired to two gateways (example with weather stations)
            if (device.bridge) {
                return device.bridge === gatewayId || DevicesModule.currentGatewayId;
            }
            return true;
        });
    }

    get getGatewayOfModuleById() {
        return (id: string): Device | undefined => {
            const mod = this.getDeviceById(id);
            if (!mod) {
                return;
            }
            return this.devices.find((device) => device.id === mod.bridge);
        };
    }

    get getGatewayModulesById() {
        return (gatewayId: string): Device[] => {
            return DevicesModule.getDeviceById(gatewayId)
                ?.modules_bridged?.map((mod) => DevicesModule.getDeviceById(mod) as Device) || [];
        };
    }

    get currentGatewayModules(): Device[] {
        return DevicesModule.getGatewayModulesById(this.currentGatewayId);
    }

    get currentGatewayRoomlessModuleIds(): string[] | undefined {
        // return modules that are not in a room
        if (this.currentGateway && this.currentGateway.modules_bridged) {
            return this.currentGateway.modules_bridged.filter((modId) => {
                const module = this.getDeviceById(modId);
                return !module?.room_id && module?.shouldShowDevice && !['NLAO', 'NLD'].includes(module.type);
            });
        }
    }

    get homelessGateways(): Device[] {
        return this.devices.filter((dev) => typeof dev.homeId === 'undefined' && !dev.bridge);
    }

    get currentGatewayModuleCount(): { wiredCount: number, wirelessCount: number } {
        let wiredCount = 0;
        let wirelessCount = 0;
        DevicesModule.currentGatewayModules.forEach((device) => {
            // only count physical modules
            if (device && !device.deviceIdIncludesHash) {
                device.isWired ? wiredCount++ : wirelessCount++;
            }
        });
        return { wiredCount, wirelessCount };
    }

    @Mutation
    SET_DEVICES(params: { devices: DeviceData [], homeId?: string }): void {
        params.devices.forEach((device) => {
            if (['NLAO', 'NLD'].includes(device.type)) {
                const mainDevice = this.devices.find((device) => device.id === device.id.split('#')[0]);
                device.room_id = device.room_id || mainDevice?.room_id;

            }
            this.devices.push(createDevice({ ...device, homeId: params.homeId }));
        });
    }

    @Action
    setDevices(params: { devices: DeviceData[], homeId?: string }): void {
        this.context.commit('SET_DEVICES', params);
    }

    @Action
    setCurrentGatewayId(gwId: string): void {
        const decodedId = decodeURIComponent(gwId);
        this.context.commit('SET_GATEWAY_ID', decodedId);
        if (this.currentGateway) {
            if (decodedId !== '') {
                DeviceGroupModule.getDeviceGroups(this.currentGateway.type);
            }
            if (!this.currentGateway.homeId) {
                OwnerModule.getOwners({ deviceId: decodedId });
            }
            if (this.currentGateway.shouldFetchSyncData) {
                this.currentGateway.loadSyncData();
            }
            if (this.currentGateway.shouldGetDebugConfigs) {
                this.currentGateway.getDebugConfigs();
            }
        }
    }

    @Action
    setCurrentModuleId(moduleId: string): void {
        this.context.commit('SET_MODULE_ID', decodeURIComponent(moduleId));
    }

    @Action
    reset(): void {
        this.context.commit('RESET_DEVICES');
    }

    @Action
    async getMacRangeCsv(): Promise<void> {
        const response = await apiService.getsupportmacrangecsv();
        const macRangeCSV = response.data.body['support-mac-range-csv'];

        const deviceInformationArray = csvToJson(macRangeCSV);
        // example of csv we can get
        // Product Type;MAC Range Start;MAC range End;Description (short);Criticality
        // NOC;70:ee:50:2f:e7:a0;;HomeKit code added;Info
        // NOC;;70:ee:50:35:e8:c8;No HZO - Potential Corrosion of SD slot;Warning
        // NATherm1;;04:00:00:3d:7f:58;Potential Buttons weakness;Info
        // NATherm1;04:00:00:11:ce:90;04:00:00:12:22:8e;Potential Buttons weakness;Info
        // NAPlug;70:ee:50:0e:5e:60;70:ee:50:0e:88:2a;Potential DOUBLE MAC;Warning
        // NRV;;09:00:00:03:c7:e9;Potential Surconso;Warning
        this.context.commit('SET_INFO_PER_MAC_RANGE_ARRAY', deviceInformationArray);
    }

    @Action
    setBehavior(
        { deviceId, behavior }:
        { deviceId: string, behavior: BehaviorFields },
    ): void {
        this.context.commit('SET_BEHAVIOR', { deviceId, behavior });
    }

    @Action
    setOffset(
        { deviceId, offsetKey, offsetValue }:
        { deviceId: string, offsetKey: OffsetKey, offsetValue: number },
    ): void {
        this.context.commit('SET_OFFSET', { deviceId, offsetKey, offsetValue });
    }

    @Action
    setHeatingAlgoType(params: { deviceId: string, heatingAlgoType: AlgoType }): void {
        const device = DevicesModule.getDeviceById(params.deviceId);
        if (device?.heating_algo) {
            // remove simple_algo and pid_algo from the object to set only the newly selected to 1 after
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { simple_algo, pid_algo, ...heating_algo } = device.heating_algo;

            (heating_algo as HeatingAlgoFields)[params.heatingAlgoType] = 1;

            this.context.commit('SET_HEATING_ALGO', { deviceId: params.deviceId, heatingAlgo: heating_algo });
        }
    }

    @Action
    setDeviceGroup(params: { deviceGroupId: string, deviceId: string }): void {
        const deviceGroup = DeviceGroupModule.deviceGroupById(params.deviceGroupId);
        if (deviceGroup) {
            apiService.adminmodifygroup({
                device_id: params.deviceId,
                new_group_name: deviceGroup.name,
            }, 'Device group successfully updated');
            this.context.commit('SET_DEVICE_GROUP', params);
        }
    }

    @Action
    async setDebugMode({ deviceId, value }: { deviceId: string, value: boolean }): Promise<void> {
        try {
            const device = DevicesModule.getDeviceById(deviceId);
            if (device) {
                await device.setDebugValue(value);
                this.context.commit('SET_DEBUG_MODE', { deviceId, value });
            }
        } catch (error) {
            return;
        }
    }

    @Action
    addDeviceInfo(params: { deviceId: string, additionalInfo: any }): void {
        this.context.commit('ADD_DEVICE_INFO', params);
    }

    @Action
    setDeviceSecret(params: { deviceId: string, deviceSecret: string }): void {
        this.context.commit('SET_DEVICE_SECRET', params);
    }

    @MutationAction
    async setDumpValue(dumpValue: string): Promise<{ dumpValue: string }> {
        return { dumpValue };
    }

    @MutationAction
    async setLocalResources(localResources: RecurseObject): Promise<{ localResources: RecurseObject }> {
        return { localResources };
    }

    @MutationAction
    async setDumpLogsValue(dumpLogsValue: string): Promise<{ dumpLogsValue: string }> {
        return { dumpLogsValue };
    }

    @MutationAction
    async setLogsValue(logsValue: any[]): Promise<{ logsValue: any[] }> {
        return { logsValue };
    }

    @MutationAction
    async setModelDumpValue(modelDumpValue: string): Promise<{ modelDumpValue: string }> {
        return { modelDumpValue };
    }

    @MutationAction
    async setDiagnosisValue(diagnosisValue: string): Promise<{ diagnosisValue: string }> {
        return { diagnosisValue };
    }

    @MutationAction
    async setStatsDumpValue(statsDumpValue: { dump: string, verbose: boolean }): Promise<{ statsDumpValue: { dump: string, verbose: boolean } }> {
        return { statsDumpValue };
    }

    @MutationAction
    async setTimelineEvents(timelineEvents: TimelineEvent[]): Promise<{ timelineEvents: TimelineEvent[] }> {
        return { timelineEvents };
    }

    // set and format migration data to use in the vue for the gateway and modules selects
    // received data:
    // [
    //     {
    //         sources: [
    //             { bridge_id: 'idA1', modules: ['idB1, idB2'] },
    //             { bridge_id: 'idA2', modules: ['idB3] },
    //         ],
    //         target: 'idB0'
    //     },
    //     {
    //         sources: [
    //             { bridge_id: 'idA1' },
    //             { bridge_id: 'idA2' },
    //         ],
    //         target: 'idA0'
    //     }
    // ]
    //
    // transformed data:
    // gatewayMigrationData = {
    //     sources: [
    //         { bridge_id: 'idA1' },
    //         { bridge_id: 'idA2' },
    //     ],
    //     target: 'idA0',
    //     selectedSource: null
    // }
    // modulesMigrationData = {
    //     sources: [
    //         { bridge_id: 'idA1', module_id: 'idB1' },
    //         { bridge_id: 'idA1', module_id: 'idB2' },
    //         { bridge_id: 'idA2', module_id: 'idB3' },
    //     ],
    //     target: 'idB0',
    //     selectedSource: null
    // }
    @MutationAction
    async setMigrationData(migrationData: MigrationData[]): Promise<{ gatewayMigrationData: TransformedMigrationData, modulesMigrationData: TransformedMigrationData[] }> {
        let gatewayMigrationData: TransformedMigrationData = { sources: [], selectedSource: null, target: '' };
        let modulesMigrationData: TransformedMigrationData[] = [];
        for (let i = 0; i < migrationData.length; i++) {
            const data = migrationData[i];
            if (data.target.includes('#')) {
                break;
            }
            // check if the current line is the line of the gateway data
            if (typeof data.sources[0].modules === 'undefined') {
                gatewayMigrationData = {
                    sources: data.sources,
                    target: data.target,
                    // for the selects in the vue
                    selectedSource: null,
                };
            } else {
                const moduleMigrationData: TransferSourceItem[] = [];
                data.sources.forEach((source) => {
                    if (source.modules) {
                        source.modules.forEach((moduleId) => {
                            // remove the entries that include a '#' like NLE that has id, id#1 and id#2 devices
                            if (!moduleId.includes('#')) {
                                moduleMigrationData.push({
                                    module_id: moduleId,
                                    bridge_id: source.bridge_id,
                                });
                            }
                        });
                    }
                });
                modulesMigrationData.push({
                    sources: moduleMigrationData,
                    target: data.target,
                    selectedSource: null,
                });
            }
        }
        // order by device type
        modulesMigrationData = modulesMigrationData.sort((a, b) => {
            const typeA = DevicesModule.getDeviceById(a.target)?.type || '';
            const typeB = DevicesModule.getDeviceById(b.target)?.type || '';
            return typeA > typeB ? 1 : -1;
        });
        return { gatewayMigrationData, modulesMigrationData };
    }

    @MutationAction
    async setGatewayMigrationData(gatewayMigrationData: TransformedMigrationData): Promise<{ gatewayMigrationData: TransformedMigrationData }> {
        return { gatewayMigrationData };
    }

    @MutationAction
    async setModulesMigrationData(modulesMigrationData: TransformedMigrationData[]): Promise<{ modulesMigrationData: TransformedMigrationData[] }> {
        return { modulesMigrationData };
    }

    @Mutation
    SET_DEVICE_SECRET(params: { deviceId: string, deviceSecret: string }): void {
        const device = DevicesModule.getDeviceById(params.deviceId);
        if (device) {
            device.secret_key = params.deviceSecret;
        }
    }

    @Mutation
    RESET_DEVICES(): void {
        this.currentGatewayId = '';
        this.devices = [];
        this.currentModuleId = '';
    }

    @Mutation
    SET_MODULE_ID(moduleId: string): void {
        this.currentModuleId = moduleId;
    }

    @Mutation
    SET_GATEWAY_ID(id: string): void {
        this.currentGatewayId = id;
    }

    @Mutation
    SET_BEHAVIOR(
        { deviceId, behavior }:
        { deviceId: string, behavior: BehaviorFields },
    ): void {
        const device = DevicesModule.getDeviceById(deviceId);
        if (device) {
            device.behavior = behavior;
        }
    }

    @Mutation
    SET_OFFSET(
        { deviceId, offsetKey, offsetValue }:
        { deviceId: string, offsetKey: OffsetKey, offsetValue: number },
    ): void {
        const device = DevicesModule.getDeviceById(deviceId);
        if (device) {
            device.offset = device.offset || {};
            device.offset[offsetKey] = offsetValue;
        }
    }

    @Mutation
    SET_HEATING_ALGO(params: { deviceId: string, heatingAlgo: HeatingAlgoFields }): void {
        const device = DevicesModule.getDeviceById(params.deviceId);
        if (device) {
            device.heating_algo = params.heatingAlgo;
        }
    }

    @Mutation
    SET_DEVICE_GROUP(params: { deviceId: string, deviceGroupId: string }): void {
        const device = DevicesModule.getDeviceById(params.deviceId);
        if (device) {
            device.device_group = params.deviceGroupId;
        }
    }

    @Mutation
    SET_DEBUG_MODE(params: { deviceId: string, value: boolean }): void {
        const device = DevicesModule.getDeviceById(params.deviceId);
        if (device) {
            device.debug_enabled = params.value;
        }
    }

    @Mutation
    SET_INFO_PER_MAC_RANGE_ARRAY(deviceInformationArray: DeviceInformation[]): void {
        this.deviceInformationArray = deviceInformationArray;
    }

    @Mutation
    ADD_DEVICE_INFO(params: { deviceId: string, additionalInfo: any }): void {
        const device = DevicesModule.getDeviceById(params.deviceId);
        if (device) {
            Object.assign(device, params.additionalInfo);
        }
    }

}

function csvToJson(csv: string, separator = ';'): DeviceInformation[] {
    const res = [];
    const lines = csv.split('\n');
    // start at i = 1 because line 0 is the headers
    for (let i = 1; i < lines.length; i++) {
        const currentLine = lines[i].split(separator);
        if (currentLine.length > 1) {
            const obj = {
                product: currentLine[0].trim(),
                description: currentLine[4].trim(),
                criticality: currentLine[5].trim().toLowerCase(),
            } as DeviceInformation;
            const rangeStart = currentLine[1].trim();
            const rangeEnd = currentLine[2].trim();
            const list = currentLine[3].trim();
            if (rangeStart || rangeEnd) {
                obj.start = macToBase10(rangeStart) || 0;
                obj.end = macToBase10(rangeEnd) || Infinity;
            }
            if (list) {
                obj.list = list.split(',').map((macString) => macToBase10(macString.trim()));
            }
            res.push(obj);
        }
    }
    return res;
}

export const DevicesModule = getModule(Devices);
